import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaUser, FaUsers } from 'react-icons/fa';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  Box,
  Button,
  Typography,
  ResponsiveDataTable,
  DataTableColumns,
} from '@tq/ui-components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';

import { CellProps } from 'react-table';
import { EmptyDataCard, hiddenTableHeaderLabel } from '../../../../components';
import { useFormatLocaleDate } from '../../../../config';
import { Comment, Consultation } from '../../../../types';

import { useCommentFilters, useViewComment } from './hooks';
import { CommentsMobileCard } from './CommentsMobileCard';
import { CommentTypeTags } from './CommentTypeTags';

export type CommentsProps = {
  consultation?: Consultation;
  comments?: Comment[];
};

export const Comments: React.FC<CommentsProps> = ({ consultation, comments }) => {
  const { t } = useTranslation('case_details_comments');

  const isMobile = useIsMobile();
  const formatLocaleDate = useFormatLocaleDate();
  const filters = useCommentFilters(comments);
  const filterColumnLayout = isMobile ? '100%' : '50% 50%';
  const onViewComment = useViewComment();
  const commentColumns: DataTableColumns<Comment>[] = [
    {
      accessor: 'isGroup',
      Cell: (cellProps: { value: string }) => (
        <Box
          color={COLOURS.gray[800]}
          alignSelf='flex-start'
          marginRight={2}
          display='inline-flex'
          fontSize={20}
        >
          {cellProps.value ? <FaUsers /> : <FaUser />}
        </Box>
      ),
      disableSortBy: true,
      Header: () => hiddenTableHeaderLabel(t('is_group_label', 'is Group')),
      width: 60,
    },
    {
      accessor: 'name',
      Cell: (cellProps: { value: string }) => (
        <>
          <Box alignSelf='flex-start' marginRight={2} display='inline-flex'>
            {cellProps.value}
          </Box>
        </>
      ),
      disableSortBy: true,
      Header: () => t('name_header', 'Name'),
      width: 'auto',
    },
    {
      accessor: 'datePublished',
      Cell: (cellProps: { value: string }) => formatLocaleDate(cellProps.value),
      Header: () => t('date_published', 'Date published'),
      width: 'auto',
    },
    {
      accessor: 'type',
      Cell: ({ row: { original } }: CellProps<Comment>) => (
        <CommentTypeTags comment={original} />
      ),
      disableSortBy: true,
      Header: () => t('type_header', 'Type'),
      width: 'auto',
    },
    {
      accessor: 'documents',
      Cell: ({ row: { original } }: CellProps<Comment>) =>
        original?.documents?.length ? (
          <Button
            ml='auto'
            size='xs'
            variant='secondary'
            onClick={() => onViewComment(original, consultation)}
          >
            {t('view', 'View')}
          </Button>
        ) : null,
      disableSortBy: true,
      Header: () => hiddenTableHeaderLabel(t('view_label', 'view')),
      width: 110,
    },
  ];
  return (
    <>
      {isMobile && (
        <Typography mx={4} mb={4}>
          <h2>{t('comments', 'Comments')}</h2>
        </Typography>
      )}
      {!comments?.length ? (
        <EmptyDataCard message={t('no_comments', 'No comments available')} />
      ) : (
        <ResponsiveDataTable
          Card={(comment) => (
            <CommentsMobileCard comment={comment} consultation={consultation} />
          )}
          filterColumnLayout={filterColumnLayout}
          filters={filters}
          size='sm'
          initialState={{
            sortBy: [
              {
                id: 'datePublished',
                desc: true,
              },
            ],
          }}
          columns={commentColumns}
          data={comments}
        />
      )}
    </>
  );
};
